import { type FC } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router';
import { Provider } from 'react-redux';

import { AuthProvider } from '#apps/client-web/features/auth/providers/auth';
import { ReactQueryClientService } from '#apps/client-web/app/services/react-query-client';
import { AppThemeProvider } from '#apps/client-web/features/settings/providers/app-theme-provider';
import { FormSettingsProvider } from '#apps/client-web/features/settings/providers/form-settings-provider';
import { appSettingsService } from '#apps/client-web/features/settings/services/app-settings';
import { UserProfilePage } from '#apps/client-web/pages/user-profile';
import { AppSettingsPage } from '#apps/client-web/pages/app-settings';
import { AuditListPage } from '#apps/client-web/pages/audit-list';
import { AuditCreatePage } from '#apps/client-web/pages/audit-create';
import { AuditDetailsPage } from '#apps/client-web/pages/audit-details';
import { AuditSettingsPage } from '#apps/client-web/pages/audit-settings/audit-settings-page';
import { NotFoundPage } from '#apps/client-web/pages/not-found';

// Audit form pages (EA)
import { AuditFormBackgroundPage } from '#apps/client-web/pages/audit-form-background';
import { AuditFormMechanicalPage } from '#apps/client-web/pages/audit-form-mechanical';
import { AuditFormMechanicalEntryPage } from '#apps/client-web/pages/audit-form-mechanical-entry';
import { AuditFormLightingTypePage } from '#apps/client-web/pages/audit-form-lighting-type';
import { AuditFormLightingTypeEntryPage } from '#apps/client-web/pages/audit-form-lighting-type-entry';
import { AuditFormLightingCountPage } from '#apps/client-web/pages/audit-form-lighting-count';
import { AuditFormLightingCountEntryPage } from '#apps/client-web/pages/audit-form-lighting-count-entry';
import { AuditFormWaterFixturePage } from '#apps/client-web/pages/audit-form-water-fixture';
import { AuditFormWaterFixtureEntryPage } from '#apps/client-web/pages/audit-form-water-fixture-entry';
import { AuditFormWindowsPage } from '#apps/client-web/pages/audit-form-windows';
import { AuditFormWindowsEntryPage } from '#apps/client-web/pages/audit-form-windows-entry';
import { AuditFormChecklistPage } from '#apps/client-web/pages/audit-form-checklist';
import { AuditFormEcmsPage } from '#apps/client-web/pages/audit-form-ecms';
import { AuditReviewSubmitPage } from '#apps/client-web/pages/audit-review-submit';

import { AppBootstrap } from './components/app-bootstrap';
import { AppLayout } from './components/app-layout';
import { routes } from './routes';
import { store } from './store';
import { useLogger } from './hooks/use-logger';

import './app.module.scss';

export const App: FC = () => {
  const logger = useLogger('app/app');

  return (
    <Provider store={store}>
      <AuthProvider>
        <HashRouter>
          <AppThemeProvider
            logger={logger}
            queryClient={ReactQueryClientService.offlineQueryClient}
            appSettingsService={appSettingsService}>
            <FormSettingsProvider
              logger={logger}
              queryClient={ReactQueryClientService.offlineQueryClient}
              appSettingsService={appSettingsService}>
              <AppBootstrap
                logger={logger}
                appSettingsService={appSettingsService}
                queryClient={ReactQueryClientService.offlineQueryClient}>
                <Routes>
                  <Route element={<AppLayout />}>
                    {/* Redirect / to /audits */}
                    <Route
                      index
                      element={<Navigate to={routes.audit.index} replace />}
                    />

                    <Route path={routes.audit.index}>
                      <Route index element={<AuditListPage />} />
                      <Route
                        path={routes.audit.create}
                        element={<AuditCreatePage />}
                      />
                      <Route path={routes.audit.details}>
                        <Route index element={<AuditDetailsPage />} />
                        <Route
                          path={routes.audit.settings}
                          element={<AuditSettingsPage />}
                        />
                      </Route>

                      <Route path={routes.audit.eaBackgroundForm}>
                        <Route index element={<AuditFormBackgroundPage />} />
                      </Route>
                      <Route path={routes.audit.eaMechanicalForm}>
                        <Route index element={<AuditFormMechanicalPage />} />
                      </Route>
                      <Route path={routes.audit.eaMechanicalFormEntry}>
                        <Route
                          index
                          element={<AuditFormMechanicalEntryPage />}
                        />
                      </Route>
                      <Route path={routes.audit.eaLightingTypeForm}>
                        <Route index element={<AuditFormLightingTypePage />} />
                      </Route>
                      <Route path={routes.audit.eaLightingTypeFormEntry}>
                        <Route
                          index
                          element={<AuditFormLightingTypeEntryPage />}
                        />
                      </Route>
                      <Route path={routes.audit.eaLightingCountForm}>
                        <Route index element={<AuditFormLightingCountPage />} />
                      </Route>
                      <Route path={routes.audit.eaLightingCountFormEntry}>
                        <Route
                          index
                          element={<AuditFormLightingCountEntryPage />}
                        />
                      </Route>
                      <Route path={routes.audit.eaWaterFixtureForm}>
                        <Route index element={<AuditFormWaterFixturePage />} />
                      </Route>
                      <Route path={routes.audit.eaWaterFixtureFormEntry}>
                        <Route
                          index
                          element={<AuditFormWaterFixtureEntryPage />}
                        />
                      </Route>
                      <Route path={routes.audit.eaWindowsForm}>
                        <Route index element={<AuditFormWindowsPage />} />
                      </Route>
                      <Route path={routes.audit.eaWindowsFormEntry}>
                        <Route index element={<AuditFormWindowsEntryPage />} />
                      </Route>
                      <Route path={routes.audit.eaChecklistForm}>
                        <Route index element={<AuditFormChecklistPage />} />
                      </Route>
                      <Route path={routes.audit.eaEcmsForm}>
                        <Route index element={<AuditFormEcmsPage />} />
                      </Route>
                      <Route path={routes.audit.eaReviewSubmit}>
                        <Route index element={<AuditReviewSubmitPage />} />
                      </Route>
                    </Route>

                    <Route
                      path={routes.userProfile.index}
                      element={<UserProfilePage />}
                    />

                    <Route
                      path={routes.appSettings.index}
                      element={<AppSettingsPage />}
                    />

                    {/* Catch all unmatched routes  */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
              </AppBootstrap>
            </FormSettingsProvider>
          </AppThemeProvider>
        </HashRouter>
      </AuthProvider>
    </Provider>
  );
};
